import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PriorityBadge } from "@/components/PriorityBadge";
import { KnowledgeBadge } from "@/components/KnowledgeBadge";
import { EvidenceLens } from "@/components/EvidenceLens";
import {
  Phone, Image as ImageIcon, Users, MapPin, Repeat, Megaphone,
  ArrowLeft, Trash2, Eye, Microscope, ShieldAlert,
} from "lucide-react";

/**
 * MUKTI 2.0 — Lead Detail (/leads/:id).
 *
 * Opens with the five mandatory sections (§11 of MUKTI 2.0 spec):
 *   1. WHAT DID MUKTI NOTICE?
 *   2. WHY DOES IT MATTER?
 *   3. WHAT SUPPORTS IT? — a stack of Evidence Lens cards
 *   4. WHAT IS CONNECTED? — compact entity chips (full graph on separate page)
 *   5. WHAT CHANGED? — mini timeline
 * Followed by officer actions: DISMISS / WATCH / INVESTIGATE.
 */
export default function LeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState("");

  const fetchLead = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/leads/${id}`);
      setLead(data);
    } catch (err) {
      if (err?.response?.status === 404) {
        toast.error("Lead not found");
        nav("/leads");
      } else {
        toast.error("Could not load lead");
      }
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { fetchLead(); }, [fetchLead]);

  const act = async (action) => {
    setActing(action);
    try {
      await api.post(`/leads/${id}/action`, { action });
      const verb = { dismiss: "dismissed", watch: "added to Watch", investigate: "moved to Investigate" }[action];
      toast.success(`Lead ${verb}`);
      fetchLead();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setActing("");
    }
  };

  const setRecordVerdict = async (signalId, verdict) => {
    try {
      await api.post(`/leads/${id}/evidence/${signalId}/finding`, { verdict });
      toast.success(verdict === "unreviewed"
        ? "Verdict cleared"
        : `Marked as ${verdict.replace("_", " ")}`);
      // Optimistic merge: patch local signal
      setLead((prev) => {
        if (!prev) return prev;
        const sigs = prev.signals.map((s) =>
          s.id === signalId
            ? {
                ...s,
                officer_verdict: verdict === "unreviewed" ? undefined : verdict,
                knowledge_type: verdict === "unreviewed" ? "source_fact" : "officer_finding",
              }
            : s,
        );
        return { ...prev, signals: sigs };
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save verdict");
    }
  };

  if (loading || !lead) {
    return <div className="py-20 text-center text-slate-400 text-sm">Loading lead…</div>;
  }

  const summary = lead.summary || {};
  const signals = lead.signals || [];
  const breakdown = lead.signal_source_breakdown || {};

  return (
    <div data-testid="lead-detail-page" className="space-y-6 max-w-4xl">
      <Link to="/leads" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-3.5 w-3.5" /> Back to Lead Room
      </Link>

      {/* Header */}
      <header className="border border-slate-200 rounded-lg bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <PriorityBadge priority={lead.priority} size="lg" />
              <KnowledgeBadge kind={lead.knowledge_type || "mukti_inference"} />
              {lead.status && lead.status !== "unreviewed" ? (
                <span
                  data-testid={`lead-status-${lead.status}`}
                  className="text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border bg-slate-50 text-slate-600 border-slate-200"
                >
                  {lead.status}
                </span>
              ) : null}
            </div>
            <h1 className="font-display text-2xl font-semibold text-slate-900 mt-2">{lead.title}</h1>
            <p className="text-xs text-slate-500 font-mono mt-1">
              last change {lead.last_change_at ? lead.last_change_at.slice(0, 10) : "—"}
              {" · "}
              Citizen {breakdown.citizen || 0} · Classified {breakdown.classified || 0} · Police {breakdown.police || 0}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline" size="sm" onClick={() => act("dismiss")}
              disabled={!!acting} data-testid="lead-action-dismiss" className="gap-1.5"
            >
              <Trash2 className="h-3.5 w-3.5" /> Dismiss
            </Button>
            <Button
              variant="outline" size="sm" onClick={() => act("watch")}
              disabled={!!acting} data-testid="lead-action-watch"
              className="gap-1.5 border-amber-200 text-amber-800 hover:bg-amber-50"
            >
              <Eye className="h-3.5 w-3.5" /> Watch
            </Button>
            <Button
              size="sm" onClick={() => act("investigate")}
              disabled={!!acting} data-testid="lead-action-investigate"
              className="bg-slate-900 hover:bg-slate-800 gap-1.5"
            >
              <Microscope className="h-3.5 w-3.5" /> Investigate
            </Button>
          </div>
        </div>
      </header>

      {/* Section 1 — WHAT DID MUKTI NOTICE? */}
      <Section number="1" title="What did MUKTI notice?">
        <p className="text-sm text-slate-700 leading-relaxed">
          Across <strong>{summary.ads_count || 0}</strong> classified advertisement(s)
          {summary.phones?.length ? <> involving <strong>{summary.phones.length}</strong> phone number(s)</> : null}
          {summary.aliases?.length ? <>, <strong>{summary.aliases.length}</strong> alias(es)</> : null}
          {summary.images?.length ? <>, and <strong>{summary.images.length}</strong> recurring photo(s)</> : null}
          {summary.localities?.length ? <>, concentrated around <strong>{summary.localities.slice(0, 2).join(", ")}</strong></> : null}
          . MUKTI connected these records because they share one or more identifiers.
        </p>
      </Section>

      {/* Section 2 — WHY DOES IT MATTER? */}
      <Section number="2" title="Why does it matter?">
        <ul className="space-y-2">
          {(lead.reasons || []).map((r) => (
            <li
              key={r.code}
              data-testid={`lead-reason-${r.code}`}
              className="flex items-start gap-3 text-sm text-slate-700"
            >
              <ReasonIcon code={r.code} />
              <span className="flex-1">{r.text}</span>
              <span className="font-mono text-[10px] text-slate-400">
                {r.support_signal_ids?.length || 0} source(s)
              </span>
            </li>
          ))}
          {!(lead.reasons || []).length ? (
            <li className="text-sm text-slate-500 italic">No reasons recorded.</li>
          ) : null}
        </ul>
      </Section>

      {/* Section 3 — WHAT SUPPORTS IT? */}
      <Section number="3" title="What supports it?">
        <div className="space-y-3">
          {signals.length === 0 ? (
            <p className="text-sm text-slate-500 italic">No supporting signals.</p>
          ) : (
            <SupportingEvidence signals={signals} summary={summary} onRecordVerdict={setRecordVerdict} />
          )}
        </div>
      </Section>

      {/* Section 4 — WHAT IS CONNECTED? */}
      <Section number="4" title="What is connected?">
        <div className="flex flex-wrap gap-2">
          {(summary.phones || []).map((p) => (
            <ConnectionChip key={p} icon={Phone} label={p} />
          ))}
          {(summary.aliases || []).map((a) => (
            <ConnectionChip key={a} icon={Users} label={a} />
          ))}
          {(summary.localities || []).map((loc) => (
            <ConnectionChip key={loc} icon={MapPin} label={loc} />
          ))}
          {(summary.publications || []).map((p) => (
            <ConnectionChip key={p} icon={Megaphone} label={p} />
          ))}
          {(summary.images || []).map((h) => (
            <ConnectionChip key={h} icon={ImageIcon} label={`photo ${h.slice(0, 10)}…`} />
          ))}
        </div>
      </Section>

      {/* Section 5 — WHAT CHANGED? */}
      <Section number="5" title="What changed? (last 30 days)">
        <Timeline signals={signals.slice(0, 10)} />
      </Section>
    </div>
  );
}

function Section({ number, title, children }) {
  return (
    <section className="border border-slate-200 rounded-lg bg-white p-5">
      <div className="flex items-center gap-3 mb-3">
        <span className="h-6 w-6 rounded-full bg-slate-900 text-white text-[11px] font-mono flex items-center justify-center">
          {number}
        </span>
        <h2 className="font-display text-sm font-semibold uppercase tracking-wider text-slate-700">{title}</h2>
      </div>
      {children}
    </section>
  );
}

const REASON_ICON = {
  repeat_phone: Phone,
  alias_switch: Users,
  reused_image: ImageIcon,
  multi_source: Megaphone,
  sustained_activity: Repeat,
  geo_concentration: MapPin,
  single_entity_match: ShieldAlert,
};

function ReasonIcon({ code }) {
  const Icon = REASON_ICON[code] || ShieldAlert;
  return (
    <span className="h-6 w-6 rounded bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
      <Icon className="h-3.5 w-3.5 text-slate-600" />
    </span>
  );
}

function ConnectionChip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-200 bg-slate-50 text-xs text-slate-700">
      <Icon className="h-3 w-3 text-slate-500" />
      <span className="font-mono">{label}</span>
    </span>
  );
}

function SupportingEvidence({ signals, summary, onRecordVerdict }) {
  // Group signals under 3 Evidence Lens cards: Phones, Images, Locality
  const cards = [];
  if (summary.phones?.length) {
    cards.push(
      <EvidenceLens
        key="phones"
        testid="evidence-phones"
        title={`Phone activity across ${summary.phones.length} number(s)`}
        icon={Phone}
        whyItMatters={`Each ad in this lead contains one of: ${summary.phones.slice(0, 3).join(", ")}.`}
        supportingRecords={signals.filter((s) => (s.phones || []).some((p) => summary.phones.includes(p)))}
        sources={Array.from(new Set(signals.map((s) => s.source).filter(Boolean)))}
        onRecordVerdict={onRecordVerdict}
      />,
    );
  }
  if (summary.images?.length) {
    cards.push(
      <EvidenceLens
        key="images"
        testid="evidence-images"
        title={`Reused photograph(s) in ${summary.ads_count || signals.length} ads`}
        icon={ImageIcon}
        whyItMatters="The same image hash appears in multiple ads — a known trafficking-ring tactic."
        supportingRecords={signals.filter((s) => (s.image_hashes || []).some((h) => summary.images.includes(h)))}
        sources={Array.from(new Set(signals.map((s) => s.source).filter(Boolean)))}
        onRecordVerdict={onRecordVerdict}
      />,
    );
  }
  if (summary.aliases?.length >= 2) {
    cards.push(
      <EvidenceLens
        key="aliases"
        testid="evidence-aliases"
        title={`${summary.aliases.length} aliases in use`}
        icon={Users}
        whyItMatters={`The same identifier posts under: ${summary.aliases.slice(0, 3).join(", ")}.`}
        supportingRecords={signals.filter((s) => (s.usernames || []).some((u) => summary.aliases.includes(u)))}
        sources={Array.from(new Set(signals.map((s) => s.source).filter(Boolean)))}
        onRecordVerdict={onRecordVerdict}
      />,
    );
  }
  if (!cards.length) {
    cards.push(
      <EvidenceLens
        key="all"
        testid="evidence-all"
        title="All supporting records"
        icon={Megaphone}
        whyItMatters="These advertisements share a common identifier."
        supportingRecords={signals}
        sources={Array.from(new Set(signals.map((s) => s.source).filter(Boolean)))}
        onRecordVerdict={onRecordVerdict}
      />,
    );
  }
  return cards;
}

function Timeline({ signals }) {
  if (!signals.length) {
    return <p className="text-sm text-slate-500 italic">No events to show.</p>;
  }
  const dateFmt = (iso) => (iso || "").slice(0, 10);
  return (
    <ol className="relative border-l-2 border-slate-200 pl-4 space-y-3" data-testid="lead-timeline">
      {signals.map((s) => (
        <li key={s.id} className="relative">
          <span className="absolute -left-[1.21rem] top-1.5 h-2 w-2 rounded-full bg-slate-400 ring-4 ring-white" />
          <div className="text-xs font-mono text-slate-500">{dateFmt(s.posted_at)}</div>
          <div className="text-sm text-slate-800 truncate">{s.title}</div>
          <div className="text-[11px] text-slate-500 flex items-center gap-1">
            {s.source ? <span>{s.source}</span> : null}
            {s.locality ? <> · <span>{s.locality}</span></> : null}
            {" · "}
            <KnowledgeBadge kind={s.knowledge_type || "source_fact"} compact />
          </div>
        </li>
      ))}
    </ol>
  );
}
